import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Tenant-scoped feature flags. Backed by `GET /api/features/me`.
 *
 * Add-ons that require a paid subscription - right now only
 * `ai_pro` - flow through here. The hook returns a definite
 * boolean for every known key so call sites can render "Upgrade" UI
 * without null-checks.
 */

const DEFAULTS = { ai_pro: false };

const FeatureCtx = createContext({ flags: DEFAULTS, loading: true, reload: () => {} });

export function FeatureProvider({ children }) {
  const [flags, setFlags] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/features/me");
      setFlags((prev) => {
        const next = { ...DEFAULTS, ...(data || {}) };
        // Cheap shallow equality - avoids re-render churn when nothing changed.
        const same = Object.keys(next).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    } catch {
      // Silent - keep last known good state; loading flips false regardless.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    // Poll every 45s - cheap 1-doc lookup on the tenant, avoids requiring a
    // full page reload after an admin toggles a flag in another tab.
    const t = setInterval(reload, 45_000);
    // Refresh immediately when the tab regains focus (common pattern:
    // admin toggles in one tab, switches to the app tab to verify).
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [reload]);

  return (
    <FeatureCtx.Provider value={{ flags, loading, reload }}>
      {children}
    </FeatureCtx.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureCtx);
}

export function useFeature(key) {
  const { flags, loading } = useFeatureFlags();
  return { enabled: !!flags?.[key], loading };
}
